import React, { FC, useCallback, useState } from 'react';

import styled from 'styled-components';

import ProductTile from '../../shared/common/ProductTile';
import NumberSpinner from '../../shared/common/NumberSpinner';
import CopiesListInfoButton from '../../shared/product/CopiesListInfoButton';
import {
    ACTION_COPY,
    ACTION_DECREASE,
    ACTION_INCREASE,
    ACTION_MOVE,
    ACTION_SET,
    PRODUCT_CLASS_CAMPAIGN
} from '../../store/constants';

import SelectionlistActionButtons from './SelectionlistActionButtons';
import RemoveFromProductlistButton from './RemoveFromProductlistButton';
import { ProductListItemProps } from '../../shared/common/ProductList';
import { ListActionTarget } from '../../../models/ListActionTarget';
import { UsageCondition } from '../../../models/Product';
import { SubscriptionPeriodType } from '../../../models/Article';
import { SingleAddOptions } from '../../../models/ListContentCheckOptions';
import { Orderlist } from '../../../models/OrderList';

const StButtonLine = styled.div`
    display: flex;
`;

const StNumberSpinner = styled(NumberSpinner)`
    flex-grow: 1;
`;

const SelectionlistProductTile: FC<ProductListItemProps> = props => {
    const {
        product,
        onAddPrice,
        onUpdateUsageCondition = async () => {},
        onCopyAmountChange,
        onAddToGroup,
        currentListId
    } = props;

    // ToDo: The hoverListener prop needed only with the setter. hoverListener={setIsDetailShown}
    //  We need to find a better solution for this. The only reason it is working is because the component re-renders so the popup is following the info button.

    const [, setIsDetailShown] = useState(false);
    const [isChangingAmount, setChangingAmount] = useState(false);

    const handleAddPrice = useCallback(
        async (options: SingleAddOptions) => {
            if (options.target === ListActionTarget.List) {
                throw new Error('illegal target "list" for selection list item');
            }

            await onAddPrice(options);
        },
        [onAddPrice]
    );

    const handleSelectPrice = useCallback(
        (usageCondition: UsageCondition, subscriptionPeriodType?: SubscriptionPeriodType) =>
            onUpdateUsageCondition(product, usageCondition, subscriptionPeriodType),
        [onUpdateUsageCondition, product]
    );

    const handleCopyAmountChange = useCallback(
        async (command: typeof ACTION_SET | typeof ACTION_INCREASE | typeof ACTION_DECREASE, amount: number) => {
            if (onCopyAmountChange) {
                setChangingAmount(true);
                try {
                    await onCopyAmountChange(product, amount, command);
                } finally {
                    setChangingAmount(false);
                }
            }
        },
        [onCopyAmountChange, product]
    );

    const handleCopyToList = useCallback(
        async (list: Orderlist) => {
            await onAddPrice({
                options: product,
                target: ListActionTarget.List,
                listId: list.id,
                action: ACTION_COPY
            });
        },
        [onAddPrice, product]
    );

    const handleMoveToList = useCallback(
        async (list: Orderlist) => {
            await onAddPrice({
                options: product,
                target: ListActionTarget.List,
                listId: list.id,
                action: ACTION_MOVE
            });
        },
        [onAddPrice, product]
    );

    const handleAddToGroup = useCallback(
        (groupId: number) => onAddToGroup?.(groupId, product),
        [onAddToGroup, product]
    );

    const isCampaignProduct = product.productClass === PRODUCT_CLASS_CAMPAIGN;

    const actionButtons = (
        <StButtonLine className="bt-line">
            {props.onRemoveProductFromList ? (
                <RemoveFromProductlistButton
                    product={product}
                    onRemoveProductFromList={props.onRemoveProductFromList}
                />
            ) : null}
            <SelectionlistActionButtons
                isCampaignProduct={isCampaignProduct}
                onCopyProductToList={handleCopyToList}
                onMoveProductToList={handleMoveToList}
                onAddToGroup={handleAddToGroup}
                showAddToGroup={!!onAddToGroup}
                hideListId={currentListId}
            />
            {product.salesStatus && product.salesStatus.key !== 'COMING_SOON' && (
                <CopiesListInfoButton
                    product={product}
                    className="fl-left sl-prd"
                    removeCopyHint={'Remove copy from selection list.'}
                    usageCondition={props.usageCondition}
                />
            )}
            <StNumberSpinner
                product={product}
                value={product.price?.copyAmount ?? 0}
                min={0}
                onChange={handleCopyAmountChange}
                disabled={isChangingAmount}
            />
        </StButtonLine>
    );

    return (
        <ProductTile
            {...props}
            showSelectionLists={false}
            actionButtons={actionButtons}
            hoverListener={setIsDetailShown}
            showSelectedPriceOnly={true}
            allowPriceSelection={true}
            onAddPrice={handleAddPrice}
            onSelectPrice={handleSelectPrice}
        />
    );
};

export default SelectionlistProductTile;
